"use client"

import * as React from "react"
import { useUser } from "@clerk/clerk-react"
import { ChevronDownIcon, MenuIcon } from "lucide-react"

import { TenantUserMenu, useActiveBranchSlug, useActiveWorkspaceSlug, useUserBranchAccess } from "@/features/auth"
import { useShowMultiBranchWorkspaceUi } from "@/features/auth/hooks/use-show-multi-branch-workspace-ui"
import { useTenantAuthFlow } from "@/features/auth/providers/tenant-auth-flow-provider"
import { useMobileContextSwitcher } from "@/features/dashboard/components/mobile-context-switcher"
import { resolveWorkspaceAccessRecord } from "@/features/dashboard/model/church-nav"
import { MOBILE_HEADER_TINT_CLASS, useMobileHeaderTintStore } from "@/features/dashboard/model/mobile-header-tint-store"
import { NotificationBell } from "@/features/notifications"
import { Bell3DIcon } from "@/shared/components/icons/bell-3d-icon"
import { useTranslations } from "@/i18n"
import { cn } from "@/shared/lib/utils"
import { isParishBranchAlias } from "@/shared/lib/branch/branch-admin-routing"
import { triggerHaptic, useSidebar } from "@ibada/ui"

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ""
  const letters = words.length > 1 ? `${words[0][0]}${words[words.length - 1][0]}` : words[0].slice(0, 2)
  return letters.toUpperCase()
}

/**
 * The single header of the phone app, on every page.
 *
 * - The menu button opens the navigation drawer (every section and settings).
 * - The greeting and church name open the church/branch switcher.
 * - The bell, then your avatar: the same account menu as on desktop (profile, preferences, sign out).
 */
/**
 * The name a phone shows for where you are working: the branch in a multi-branch church working in one branch,
 * otherwise the church itself (mono churches and the organization scope).
 */
export function useMobileContextName(isParish: boolean): string {
  const { authMe } = useTenantAuthFlow()
  const workspaceSlug = useActiveWorkspaceSlug() ?? ""
  const branchSlug = useActiveBranchSlug() ?? ""
  const { accessibleBranches } = useUserBranchAccess()
  const showMultiBranchUi = useShowMultiBranchWorkspaceUi()

  const access = React.useMemo(() => resolveWorkspaceAccessRecord(authMe, workspaceSlug), [authMe, workspaceSlug])
  const churchName = access?.workspace.displayName?.trim() || access?.workspace.name || workspaceSlug
  const branchName =
    showMultiBranchUi && !isParish && !isParishBranchAlias(branchSlug)
      ? accessibleBranches.find((branch) => branch.slug === branchSlug)?.name?.trim()
      : undefined
  return branchName || churchName
}

export function MobileAppHeader({ isParish }: { isParish: boolean }) {
  const t = useTranslations("dashboard.mobileHome")
  const tSwitcher = useTranslations("churchNav.switcher")
  const { setOpenMobile } = useSidebar()
  const { user } = useUser()
  const switcher = useMobileContextSwitcher()
  const contextName = useMobileContextName(isParish)
  const firstName = user?.firstName?.trim()
  const tinted = useMobileHeaderTintStore((state) => state.tintRequests > 0)

  return (
    <header className='relative isolate flex shrink-0 items-center gap-3 bg-[var(--app-canvas)] px-4 pt-[calc(var(--mobile-header-pt)+1.25rem)] pb-2 md:hidden'>
      {/* The tint is a layer over the canvas, exactly like a hero band's top over the page, so the two match. */}
      {tinted ? <span aria-hidden className={cn("absolute inset-0 -z-10", MOBILE_HEADER_TINT_CLASS)} /> : null}
      <button
        type='button'
        aria-label={t("openMenu")}
        data-tour='mobile-menu'
        onClick={() => {
          triggerHaptic("light")
          setOpenMobile(true)
        }}
        className='inline-flex size-11 shrink-0 items-center justify-center rounded-full border border-border bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      >
        <MenuIcon aria-hidden className='size-5 text-foreground' />
      </button>
      <button
        type='button'
        aria-label={`${tSwitcher("switchWorkspace")}: ${contextName}`}
        onClick={switcher.open}
        className='flex min-h-11 min-w-0 flex-1 flex-col justify-center rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      >
        <span className='truncate text-[13px] text-muted-foreground'>
          {firstName ? t("greeting", { name: firstName }) : t("greetingNoName")}
        </span>
        <span className='flex min-w-0 items-center gap-1'>
          <span className='truncate text-lg leading-tight font-extrabold text-foreground'>{contextName}</span>
          <ChevronDownIcon aria-hidden className='size-4 shrink-0 text-muted-foreground' />
        </span>
      </button>
      <div className='flex shrink-0 items-center gap-2'>
        <NotificationBell
          triggerClassName='size-11 rounded-full border border-border bg-card [&_svg]:size-6'
          icon={<Bell3DIcon className='size-6' />}
        />
        <TenantUserMenu
          context='branch'
          afterSignOutUrl='/login'
          className='flex size-11 items-center justify-center [&_.cl-avatarBox]:size-10'
        />
      </div>
      {switcher.element}
    </header>
  )
}
